import type { ChatImage, DocumentReference, QueuedChatMessage } from '@/components/layout/chat-pane.types'

export type QueueEditSkill = { id: string; name: string }

/** Composer draft stashed while a queued row is open in the composer. */
export type QueueEditSession = {
  id: string
  stashedText: string
  stashedImages: ChatImage[]
  stashedReferences: DocumentReference[]
  stashedSkill: QueueEditSkill | null
}

function newQueueItemId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `q-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function captureComposerQueueItem(opts: {
  text: string
  images?: ChatImage[]
  id?: string
}): QueuedChatMessage | null {
  const text = String(opts.text || '').trim()
  const images = Array.isArray(opts.images) ? opts.images : []
  if (!text && images.length === 0) return null
  return { id: opts.id ?? newQueueItemId(), text, images }
}

export function applyQueueTextUpdate(
  queue: QueuedChatMessage[],
  id: string,
  text: string,
): QueuedChatMessage[] {
  return queue.map((row) => (row.id === id ? { ...row, text } : row))
}

export function removeQueuedById(
  queue: QueuedChatMessage[],
  id: string,
): QueuedChatMessage[] {
  return queue.filter((row) => row.id !== id)
}

/**
 * Open a queued row in the composer.
 * Switching rows commits the previous composer text and keeps the original draft stash.
 */
export function openQueueEditSession(opts: {
  item: QueuedChatMessage
  session: QueueEditSession | null
  /** Serialized composer text. Used to commit when switching rows. */
  composerText: string
  /** Plain draft text (no chip markers). Stashed only when starting fresh. */
  draftText: string
  draftImages: ChatImage[]
  draftReferences: DocumentReference[]
  draftSkill: QueueEditSkill | null
}): {
  focusOnly: boolean
  session: QueueEditSession
  composerText: string
  commit: { id: string; text: string } | null
} | null {
  if (!opts.item.id) return null
  if (opts.session?.id === opts.item.id) {
    return {
      focusOnly: true,
      session: opts.session,
      composerText: opts.item.text,
      commit: null,
    }
  }
  const switching = opts.session
  const commitText = switching ? opts.composerText.trim() : ''
  return {
    focusOnly: false,
    session: {
      id: opts.item.id,
      stashedText: switching ? switching.stashedText : opts.draftText,
      stashedImages: switching ? switching.stashedImages : opts.draftImages,
      stashedReferences: switching ? switching.stashedReferences : opts.draftReferences,
      stashedSkill: switching ? switching.stashedSkill : opts.draftSkill,
    },
    composerText: opts.item.text,
    commit: switching && commitText ? { id: switching.id, text: commitText } : null,
  }
}

/**
 * Leave the edit. Save writes composer text onto the row.
 * Empty text is not saved unless the row already has images.
 * Cancel leaves the row text alone and always restores the stash.
 */
export function closeQueueEditSession(opts: {
  session: QueueEditSession
  mode: 'save' | 'cancel'
  composerText: string
  rowHasImages: boolean
}):
  | { closed: false }
  | {
      closed: true
      savedText: string | null
      composerText: string
      composerImages: ChatImage[]
    } {
  const text = opts.composerText.trim()
  if (opts.mode === 'save' && !text && !opts.rowHasImages) {
    return { closed: false }
  }
  return {
    closed: true,
    savedText: opts.mode === 'save' ? text : null,
    composerText: opts.session.stashedText,
    composerImages: opts.session.stashedImages,
  }
}

export function queuedMessagePreview(item: QueuedChatMessage): string {
  if (item.text) return item.text
  if (item.images.length === 1) return '1 image'
  if (item.images.length > 1) return `${item.images.length} images`
  return ''
}

export function resolveStagedImagesForSend(opts: {
  imagesOverride?: ChatImage[]
  composerImages: ChatImage[]
  skipComposer: boolean
}): ChatImage[] {
  if (opts.imagesOverride !== undefined) return opts.imagesOverride
  if (opts.skipComposer) return []
  return opts.composerImages
}
